import { useState } from 'react';
import Button from 'components/button';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import { fillText } from 'components/invoiceImport/invoiceImportText';
import JournalErrorGroup from './JournalErrorGroup';

/** Grey blocks while the journal loads. */
export const JournalSkeleton = ({ label }) => (
  <div role="status" aria-label={label} className="flex flex-col gap-3">
    {[0, 1, 2].map((index) => (
      <div key={index} className="h-20 rounded-lg skeleton-bar" />
    ))}
  </div>
);

/** "Le journal n'a pas pu être chargé" and a way to read it again. */
export const JournalLoadFailed = ({ text, onReload }) => (
  <div
    role="alert"
    className="flex flex-col items-center gap-3 py-16 text-center"
  >
    <div className="text-gray-700">
      {text('loadFailed', 'Le journal n’a pas pu être chargé')}
    </div>
    <Button color="SECONDARY" size="SM" onClick={onReload}>
      {text('reload', 'Recharger')}
    </Button>
  </div>
);

/**
 * The "À corriger" tab of the import journal (JBWEB-1280, mockup 2.8): the
 * month's invoices in error, one block per cause. The first block is unfolded.
 */
const JournalErrorsTab = ({
  errors,
  text,
  month,
  onRetry,
  isRetryDisabled,
  onViewInvoice,
}) => {
  // Codes whose fold the admin changed; every other block keeps its default.
  const [toggled, setToggled] = useState({});

  if (errors.status === 'loading') {
    return (
      <JournalSkeleton label={text('loading', 'Chargement du journal…')} />
    );
  }
  if (errors.status === 'failed') {
    return <JournalLoadFailed text={text} onReload={errors.reload} />;
  }

  const { total, groups } = errors.data;
  if (groups.length === 0) {
    return (
      <CompactEmptyState
        glyph="badgeCheck"
        compact
        title={text('emptyTitle', 'Aucune facture à corriger')}
        description={fillText(
          text('emptyText', 'Aucune facture de {month} n’est en erreur.'),
          { month },
        )}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-gray-600">
        {total === 1
          ? text(
              'headerOne',
              '1 facture est en erreur. Corrigez sa cause, puis réessayez.',
            )
          : fillText(
              text(
                'headerMany',
                '{count} factures sont en erreur. Elles sont regroupées par cause : corrigez la cause, puis réessayez.',
              ),
              { count: total },
            )}
      </p>
      {groups.map((group, index) => {
        const isOpen = Boolean(toggled[group.code]) !== (index === 0);
        return (
          <div
            key={group.code}
            className="motion-safe:animate-fadeUp"
            style={{ animationDelay: `${Math.min(index, 9) * 30}ms` }}
          >
            <JournalErrorGroup
              group={group}
              text={text}
              isOpen={isOpen}
              onToggle={() =>
                setToggled((previous) => ({
                  ...previous,
                  [group.code]: !previous[group.code],
                }))
              }
              onRetry={onRetry}
              isRetryDisabled={isRetryDisabled}
              onViewInvoice={onViewInvoice}
            />
          </div>
        );
      })}
    </div>
  );
};

export default JournalErrorsTab;
